import classNames from 'classnames';
import type { CSSProperties } from 'react';

import { PictureComponentV2 } from 'app/components/commons/PictureComponentV2/PictureComponentV2';
import type { PictureView } from 'app/typings/core/common/pictureView/pictureView';

import styles from './TripleImages.module.scss';

const DEFAULT_IMAGE_SIZE = 32;
const SIDE_IMAGE_RATIO = 30 / DEFAULT_IMAGE_SIZE;

type TripleImagesProps = {
  images: PictureView[];
  width?: number;
  height?: number;
};

type TripleImagesStyle = CSSProperties & {
  '--triple-image-width'?: string;
  '--triple-image-height'?: string;
  '--triple-side-image-width'?: string;
  '--triple-side-image-height'?: string;
  '--triple-left-offset-x'?: string;
  '--triple-right-offset-x'?: string;
  '--triple-offset-y'?: string;
};

export const TripleImages = ({ images, width, height }: TripleImagesProps) => {
  const hasCustomSize = width !== undefined || height !== undefined;
  const imageWidth = width ?? height ?? DEFAULT_IMAGE_SIZE;
  const imageHeight = height ?? width ?? DEFAULT_IMAGE_SIZE;
  const sideImageWidth = Math.round(imageWidth * SIDE_IMAGE_RATIO);
  const sideImageHeight = Math.round(imageHeight * SIDE_IMAGE_RATIO);
  const style: TripleImagesStyle | undefined = hasCustomSize
    ? {
        '--triple-image-width': `${imageWidth}px`,
        '--triple-image-height': `${imageHeight}px`,
        '--triple-side-image-width': `${sideImageWidth}px`,
        '--triple-side-image-height': `${sideImageHeight}px`,
      }
    : undefined;

  return (
    <div className={styles.tripleImages} style={style}>
      <div className={styles.imagesContainer}>
        {images.slice(0, 3).map((image, index) => {
          const isCenterImage = index === 0;
          const pictureWidth = isCenterImage ? imageWidth : sideImageWidth;
          const pictureHeight = isCenterImage ? imageHeight : sideImageHeight;

          return (
            <div
              key={`${image.id}-${index}`}
              className={classNames({
                [styles.leftImage]: index === 1,
                [styles.centerImage]: isCenterImage,
                [styles.rightImage]: index === 2,
              })}
            >
              <PictureComponentV2
                path={image.path}
                alt={image.alt}
                width={hasCustomSize ? pictureWidth : undefined}
                height={hasCustomSize ? pictureHeight : undefined}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
